import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AdminPanelNav } from '../AdminPanelPage';
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Icon,
  Loading,
  Select,
  SelectOptionGroupType,
  SelectOptionType,
  useSidebarNav,
} from '@aidkitorg/component-library';
import { LanguageDropdown } from '../common/LanguageDropdown';
import { useTheme } from '../../Contexts/ThemeContext';
import { useLocalizedStrings } from '../../Localization';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import InterfaceContext, { PublicConfigurationContext, UserInfoContext } from '../../Context';
import {
  ApplicantRightPanel,
  ApplicantSubtabsParams,
  ResizablePanelControlContext,
  useApplicantSubtabs,
} from '../../Components/ThreeColumnPage';
import { ApplicantProvider } from '../../Contexts/ApplicantContext';
import { AssigneeSelect, resolveAssigneeField, ReviewDetails, ViewPaymentsButton } from '../../ReviewWrapper';
import { snakeToEnglish } from '../../Util';
import { AICoachProvider } from '../../AICoach/AICoachContext';
import { AICoachChatWindow } from '../../AICoach/AICoachUI';

type AdminPanelApplicantPageProps = {
  uid: string;
  /** Set when this applicant is a pending/not-yet-submitted (DynamoDB-backed) application,
   * viewed via the read-only /ua/ route. Mirrors ApplicantPage's own uid/dynamoAppId split. */
  dynamoAppId?: string;
  review: ReviewDetails | undefined;
  isLoading: boolean;
  renderMainContent: () => React.ReactNode;
  nav: NavigationNode[];
  subsurvey: string | undefined;
  setSubsurvey: (s: string) => void;
  info: Record<string, any>;
  applicantTitle: string;
  applicantContext: { info: Record<string, any>; update: (update: Record<string, any>) => void };
  disallowed: string[] | null;
} & Pick<
  ApplicantSubtabsParams,
  | 'fetchedInfo'
  | 'loggedInConfig'
  | 'fraudFlagsLength'
  | 'setFraudFlagsLength'
  | 'relatedSupportCases'
  | 'roles'
  | 'publicConfig'
  | 'saveInfo'
  | 'commsChannels'
>;

export function AdminPanelApplicantPage({
  uid,
  dynamoAppId,
  review,
  isLoading,
  renderMainContent,
  nav,
  subsurvey,
  setSubsurvey,
  info,
  applicantTitle,
  applicantContext,
  disallowed,
  fetchedInfo,
  loggedInConfig,
  fraudFlagsLength,
  setFraudFlagsLength,
  relatedSupportCases,
  roles,
  publicConfig,
  saveInfo,
  commsChannels,
}: AdminPanelApplicantPageProps) {
  const L = useLocalizedStrings();
  const history = useHistory();
  const { pathname, hash, search } = useLocation();
  const interfaceContext = useContext(InterfaceContext);
  const { languages } = useTheme();

  const { state: sidebarState, setOpen: setSidebarOpen } = useSidebarNav();
  const [blockRightNav, setBlockRightNav] = useState(true);
  const [showAdminNav, setShowAdminNav] = useState(false);
  const [headerHeight, setHeaderHeight] = useState<number>(150);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevSidebarStateRef = useRef(sidebarState);

  const applicantInfoPanelLabel = L.menu?.applicant_info_panel;
  const config = useContext(PublicConfigurationContext);
  const { tags } = useContext(UserInfoContext);

  const baseTabs = useApplicantSubtabs({
    uid,
    disallowed,
    commsChannels,
    fetchedInfo,
    loggedInConfig,
    fraudFlagsLength,
    setFraudFlagsLength,
    relatedSupportCases,
    roles,
    publicConfig,
    saveInfo,
    dynamoAppId,
    info,
  });

  const tabs = useMemo(() => {
    const aiChatEnabled = !!config?.capabilities?.appReviewAssistant && !config.capabilities.appReviewAssistant.disabled;
    return [
      ...baseTabs,
      {
        key: 'review-ai-assistant',
        name: 'AI Chat',
        show: aiChatEnabled && !!(tags?.includes('ai_coach') || tags?.includes('admin')),
        children: (
          <div className="flex flex-col -mx-6 h-[93dvh]">
            <AICoachProvider application="reviewCoach" applicantUid={uid} sessionName={`review_assistant_session_uid_${uid}`}>
              <AICoachChatWindow embedded skipTerms audience="reviewer" onClickMinimize={() => {}} />
            </AICoachProvider>
          </div>
        ),
      },
    ];
  }, [baseTabs, config?.capabilities?.appReviewAssistant, tags, uid]);

  const subsurveyOptions = useMemo<SelectOptionType[] | SelectOptionGroupType[]>(() => {
    const getLocalizedText = (text: { en?: string; [key: string]: string | undefined }): string =>
      text[interfaceContext.lang] || text['en'] || '';

    const getNodeLabel = (node: NavigationNode): string => {
      if (node.subsurveyTitle) return getLocalizedText(node.subsurveyTitle as any);
      if (typeof node.name === 'string') {
        if (node.name.startsWith('/p/')) {
          return node.name.slice(3).split('/').map(part => snakeToEnglish(part)).join(' / ');
        }
        return node.name;
      }
      return getLocalizedText(node.name as any);
    };

    // Include any nav node that has a navigable applicant-scoped href (no pure section anchors, no global dashboards).
    // /ao/ (applicant portal pages) is excluded for unsubmitted applicants (dynamoAppId set): the portal
    // reads the applicant's info from Postgres, which has no row yet for a DynamoDB-only pending application,
    // so there's nothing for those pages to show until the applicant submits.
    const isNavigable = (href: string) =>
      !!href && !href.startsWith('#') &&
      (dynamoAppId ? ['/a/', '/ua/', '/ad/', '/payments/'] : ['/a/', '/ua/', '/ad/', '/ao/', '/payments/']).some(p => href.startsWith(p));

    const hasAnyFolder = (nodes: NavigationNode[]): boolean =>
      nodes.some(n => n.isFolder || (n.subnodes && hasAnyFolder(n.subnodes)));

    const isPaymentHref = (href: string) => href.startsWith('/payments/');

    if (!hasAnyFolder(nav)) {
      const options: SelectOptionType[] = [];
      const extract = (nodes: NavigationNode[]) => {
        for (const node of nodes) {
          if (isNavigable(node.href)) options.push({ value: node.href, label: getNodeLabel(node) });
          if (node.subnodes) extract(node.subnodes);
        }
      };
      extract(nav);

      const paymentOpts = options.filter(o => isPaymentHref(o.value as string));
      const otherOpts = options.filter(o => !isPaymentHref(o.value as string));
      if (paymentOpts.length > 0) {
        const groups: SelectOptionGroupType[] = [{ label: L.admin_panel.payment_details_group, options: paymentOpts }];
        if (otherOpts.length > 0) groups.push({ label: '', options: otherOpts });
        return groups;
      }
      return options;
    }

    const groups: SelectOptionGroupType[] = [];
    const ungrouped: SelectOptionType[] = [];

    const processNodes = (nodes: NavigationNode[], parentGroup?: SelectOptionGroupType) => {
      for (const node of nodes) {
        if (node.isFolder) {
          const label = getNodeLabel(node);
          if (!label) {
            // Unnamed collection — flatten its children into the current level
            if (node.subnodes) processNodes(node.subnodes, parentGroup);
            continue;
          }
          const group: SelectOptionGroupType = { label, options: [] };
          groups.push(group);
          if (node.subnodes) processNodes(node.subnodes, group);
        } else if (isNavigable(node.href)) {
          const option = { value: node.href, label: getNodeLabel(node) };
          if (parentGroup) parentGroup.options.push(option);
          else ungrouped.push(option);
        } else if (node.subnodes) {
          processNodes(node.subnodes, parentGroup);
        }
      }
    };

    processNodes(nav);
    const paymentOpts = ungrouped.filter(o => isPaymentHref(o.value as string));
    const nonPaymentUngrouped = ungrouped.filter(o => !isPaymentHref(o.value as string));
    if (nonPaymentUngrouped.length > 0) groups.unshift({ label: '', options: nonPaymentUngrouped });
    if (paymentOpts.length > 0) groups.unshift({ label: L.admin_panel.payment_details_group, options: paymentOpts });
    return groups.filter(g => g.options.length > 0);
  }, [nav, interfaceContext.lang, L.admin_panel.payment_details_group, dynamoAppId]);

  const flatSubsurveyOptions = useMemo<SelectOptionType[]>(
    () => subsurveyOptions.flatMap(opt => 'options' in opt ? opt.options : [opt]),
    [subsurveyOptions]
  );

  // Convert a nav href (e.g. /a/:uid/path, /ua/:dynamoAppId/path, /ad/:uid/path, /payments/:uid)
  // to the equivalent admin-panel route. /ua/ hrefs point at pending (unsubmitted, DynamoDB-backed)
  // applications, so they map to the read-only /admin-panel/ua/ route to preserve that handling
  // (rather than falling back to /admin-panel/a/, which would silently drop read-only/unsubmitted
  // behavior for the rest of the session).
  const toAdminPanelPath = useCallback((href: string): string => {
    return '/admin-panel' + href;
  }, []);

  const selectedSubsurveyOption = useMemo(() => {
    const safeDecode = (s: string) => { try { return decodeURIComponent(s); } catch { return s; } };
    const normalize = (p: string) => p.replace('/admin-panel', '').replace(/^\/ua\//, '/a/');
    const normalizeValue = (v: unknown) => normalize(String(v));
    // Try exact match first (path + hash), then fall back to path-only match
    const fullPath = normalize(safeDecode(pathname + hash));
    const pathOnly = normalize(safeDecode(pathname));
    return flatSubsurveyOptions.find(o => normalizeValue(o.value) === fullPath)
      ?? flatSubsurveyOptions.find(o => normalizeValue(o.value) === pathOnly);
  }, [flatSubsurveyOptions, pathname, hash]);

  // Derive the current section label directly from the URL so breadcrumbs and the
  // dropdown show the correct value immediately, before nav finishes loading.
  const currentSectionLabel = useMemo(() => {
    if (selectedSubsurveyOption) {
      return typeof selectedSubsurveyOption.label === 'string'
        ? selectedSubsurveyOption.label
        : selectedSubsurveyOption.value as string;
    }
    if (pathname.match(/\/payments\//)) return 'Payments';
    const adMatch = pathname.match(/\/admin-panel\/ad\/[^/]+\/(.+)$/);
    if (adMatch) return adMatch[1].split('/').map(snakeToEnglish).join(' / ');
    if (subsurvey) return subsurvey.split('/').map(snakeToEnglish).join(' / ');
    return '';
  }, [selectedSubsurveyOption, pathname, subsurvey]);

  const handleSubsurveyChange = useCallback((option: SelectOptionType) => {
    const href = option.value as string;
    // Keep subsurvey state in sync for /a/ and /ua/ navigations
    if (href.startsWith('/a/') || href.startsWith('/ua/')) {
      setSubsurvey(href.split('/').slice(3).join('/'));
    }
    // Remember the current page as the one-hop "back" target for the review nav.
    // Stored in React Router location state (read by ReviewWrapper's ReviewNav)
    // rather than a `back` URL param, so switching subsurveys doesn't grow the
    // URL. `back` is only ever meant to point one hop backward. Note: location
    // state is dropped on a hard refresh, in which case Back falls back to
    // reopening the left nav.
    const backTarget = window.location.pathname + window.location.search + window.location.hash;
    const adminPath = toAdminPanelPath(href);
    const [pathPart, hashPart] = adminPath.split('#');
    history.push({
      pathname: pathPart,
      hash: hashPart ? `#${hashPart}` : '',
      state: { back: backTarget },
    });
  }, [history, toAdminPanelPath, setSubsurvey]);

  const displayName = info?.legal_name || applicantTitle || L.applicant.applicant;

  const reassignReviewerConfig = review?.reassignReviewer;
  const resolvedField = resolveAssigneeField(reassignReviewerConfig?.assigneeField, info || {});
  const currentAssigneeUid = resolvedField && info?.[resolvedField]
    ? String(info[resolvedField]).trim()
    : undefined;

  // Stay on the read-only /ua/ route for pending (unsubmitted) applications, rather than
  // linking to the non-readonly /a/ route and silently losing read-only handling.
  const applicantHref = dynamoAppId ? `/admin-panel/ua/${dynamoAppId}` : `/admin-panel/a/${uid}`;

  const applicantHeading = (
    <div className="flex flex-col min-w-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{L.applicant.applicant}</span>
      <span className="text-lg font-semibold text-gray-900 truncate">{displayName}</span>
    </div>
  );

  const hasVisibleTabs = tabs.some(t => t.show);

  const pageActionButtons = useMemo(() => {
    const buttons: React.ReactElement[] = [];

    if (reassignReviewerConfig) {
      buttons.push(
        <AssigneeSelect
          key="reassign-reviewer"
          uid={uid}
          reassignReviewerConfig={reassignReviewerConfig}
          currentAssigneeUid={currentAssigneeUid}
          info={info || {}}
        />
      );
    }

    if (review?.includePayments) {
      buttons.push(
        <ViewPaymentsButton key="view-payments" uid={uid} />
      );
    }

    if (!disallowed?.includes('tabs') && hasVisibleTabs) {
      buttons.push(
        <div key="show-tools" className="self-end">
          <Button
            onClick={() => setBlockRightNav(prev => !prev)}
            title={blockRightNav ? 'Open applicant info panel' : 'Close applicant info panel'}
            aria-label={blockRightNav ? 'Open applicant info panel' : 'Close applicant info panel'}
            icon={blockRightNav
              ? <Icon name='ChevronLeftIcon' className="!h-4 !w-4 shrink-0" role='decorative' strokeWidth={1} />
              : <Icon name='ChevronRightIcon' className="!h-4 !w-4 shrink-0" role='decorative' strokeWidth={1} />}
            iconPlacement="right"
            variant="secondary"
            size="md"
            className="!rounded-md !border-gray-300 !font-normal"
          >
            {blockRightNav ? L.admin_panel.reviews.show_tools : L.admin_panel.reviews.hide_tools}
          </Button>
        </div>
      );
    }

    return buttons.length > 0 ? buttons : undefined;
  }, [disallowed, blockRightNav, reassignReviewerConfig, currentAssigneeUid, uid, review, hasVisibleTabs, info, L.admin_panel.reviews.show_tools, L.admin_panel.reviews.hide_tools]);

  // When we first toggle into a review view, we should close the right nav.
  // It can be opened manually. On a review, honor expandToolsPanelByDefault.
  useEffect(() => {
    if (!hasVisibleTabs) {
      setBlockRightNav(true);
      return;
    }
    if (review) {
      setBlockRightNav(!review.expandToolsPanelByDefault);
    } else if (!isLoading) {
      setBlockRightNav(false);
    }
  }, [review, isLoading, hasVisibleTabs]);

  const adminNavActive = !!review && showAdminNav;

  useEffect(() => {
    if (adminNavActive) {
      setSidebarOpen(true);
    }
  }, [adminNavActive, setSidebarOpen]);

  useEffect(() => {
    const prevState = prevSidebarStateRef.current;
    prevSidebarStateRef.current = sidebarState;

    if (adminNavActive && prevState === 'expanded' && sidebarState === 'collapsed') {
      setShowAdminNav(false);
    }
  }, [adminNavActive, sidebarState]);

  const rightPanel = (
    <ApplicantRightPanel
      key={`right-panel-${blockRightNav}`}
      controlled
      defaultSubtabKey="notes"
      blockRightNav={blockRightNav}
      setBlockRightNav={setBlockRightNav}
      containerRef={containerRef}
      topOffset={headerHeight + 50}
      dir={interfaceContext?.direction ?? 'ltr'}
      review={review}
      label={applicantInfoPanelLabel}
      tabs={tabs}
    />
  );

  return (
    <ApplicantProvider context={applicantContext}>
      <ResizablePanelControlContext.Provider value={{
        showLeftNav: () => setShowAdminNav(true),
        hideLeftNav: () => setShowAdminNav(false),
        isLeftNavVisible: !review || showAdminNav,
        showRightNav: () => setBlockRightNav(false),
        hideRightNav: () => setBlockRightNav(true),
        isRightNavVisible: !blockRightNav,
        hasRightNavContent: hasVisibleTabs,
        headerHeight,
        setHeaderHeight,
      }}>
        <div className="flex flex-1 min-w-0 h-screen bg-gray-50 admin-panel-print-root" ref={containerRef}>
          {/* Admin sidebar */}
          {(!review || showAdminNav) && !isLoading && <aside className="flex-shrink-0 noprint">
            <AdminPanelNav />
          </aside>}

          {/* Main column */}
          <div className="flex-1 flex flex-col min-h-0 admin-panel-main-column">
            {/* Header */}
            <div className="w-full border-b bg-white flex-shrink-0 noprint">
              <div className="flex flex-wrap items-center px-4 py-3 gap-2">
                {flatSubsurveyOptions.length > 1 ? (
                  <nav aria-label="breadcrumb" className="png-hide flex-1 min-w-0">
                    <BreadcrumbList dir={interfaceContext?.direction ?? 'ltr'}>
                      <BreadcrumbItem className="min-w-0">{applicantHeading}</BreadcrumbItem>
                      <BreadcrumbSeparator dir={interfaceContext?.direction ?? 'ltr'} />
                      <BreadcrumbItem className="min-w-0">
                        <Select
                          label=""
                          hideLabel={true}
                          description=""
                          options={subsurveyOptions}
                          value={selectedSubsurveyOption ?? (currentSectionLabel ? { value: pathname, label: currentSectionLabel } : undefined)}
                          onChange={handleSubsurveyChange}
                          placeholder="Select a section"
                          portal={true}
                          size="lg"
                          groupedOptionsCollapsible={true}
                          anchor="bottom start"
                        />
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </nav>
                ) : (
                  <nav aria-label="breadcrumb" className="png-hide flex-1 min-w-0">
                    <BreadcrumbList dir={interfaceContext?.direction ?? 'ltr'}>
                      <BreadcrumbItem className="min-w-0">
                        {currentSectionLabel
                          ? <BreadcrumbLink to={applicantHref} className="min-w-0 hover:no-underline [&>span]:min-w-0">{applicantHeading}</BreadcrumbLink>
                          : applicantHeading}
                      </BreadcrumbItem>
                      {currentSectionLabel && (
                        <>
                          <BreadcrumbSeparator dir={interfaceContext?.direction ?? 'ltr'} />
                          <BreadcrumbItem className="min-w-0">
                            <BreadcrumbPage className="text-base truncate">{currentSectionLabel}</BreadcrumbPage>
                          </BreadcrumbItem>
                        </>
                      )}
                    </BreadcrumbList>
                  </nav>
                )}
                {pageActionButtons && (
                  <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                    {pageActionButtons.map((b, i) => <React.Fragment key={i}>{b}</React.Fragment>)}
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            {/* admin-panel-main-content: scopes the print-unclipping rules in index.css to this
                page only, since #main-content is also used (with a different structure) by
                ThreeColumnPage and DistroReviewPage. */}
            <main
              id="main-content"
              className={`admin-panel-main-content relative flex-1 min-h-0 p-0 bg-white${review ? ' overflow-hidden' : ' overflow-auto'}`}
            >
              <div className={`absolute inset-0${review ? '' : ' overflow-auto'}`}>
                <Loading loading={isLoading}>
                  {renderMainContent()}
                </Loading>
              </div>
            </main>

            {/* Footer */}
            {!review && <footer className="w-full flex px-4 pb-2 flex-shrink-0 noprint">
              <div className="flex items-center border-t pt-2 w-full gap-4">
                <div className="flex flex-row space-x-4">
                  <Button variant="link" href="https://www.aidkit.com/privacy-policy" openInNewWindow>{L.privacy_policy}</Button>
                  <Button variant="link" href="/tos.html" openInNewWindow>{L.terms_and_conditions}</Button>
                </div>
                <div className="ml-auto min-w-40">
                  <LanguageDropdown languages={languages} />
                </div>
              </div>
            </footer>}
          </div>

          {/* Right panel — full-height flex sibling, same pattern as ThreeColumnPage */}
          <div className="noprint">
            {rightPanel}
          </div>
        </div>
      </ResizablePanelControlContext.Provider>
    </ApplicantProvider>
  );
}
